import type { RecurrenceOverride, RecurrenceRule, Task } from "@app/core";
import { useMemo, useRef } from "react";
import { type DayCol, type RuleExpansionCache, expandDayCols, sameTasks } from "./build-days.js";
import { getCalendarRuleExpansionCache } from "./calendar-expansion-cache.js";
import { useDayWindow } from "./useDayWindow.js";

type Rules = Record<string, RecurrenceRule>;
type Overrides = Record<string, ReadonlyArray<RecurrenceOverride>>;

/** True when two day-columns render identically (see `sameTasks` — positional
 *  ref fast-path + value fallback). `timed` derives from `tasks`, so it needn't
 *  be compared. */
function sameDay(a: DayCol, b: DayCol): boolean {
  return sameTasks(a.tasks, b.tasks);
}

/**
 * Combines the stable wide window (useDayWindow) with a per-day reference cache.
 * `tasks` should already be view-filtered AND memoized by the caller.
 *
 * The whole window is re-expanded whenever the data or the window changes, but a
 * day whose content is unchanged KEEPS its previous `DayCol` object reference, so
 * the `React.memo`'d `DayColumn` for it skips re-rendering. This holds across BOTH
 * a window recenter (overlapping days unchanged) AND a data edit (only the day(s)
 * the edit actually touched re-render) — a single color change or drag no longer
 * forces all ~3×visibleCount mounted columns (and their blocks) through framer.
 *
 * The expansion itself is also incremental: a persistent `ruleCache` memoizes each
 * series' virtuals per rule (keyed on source-task / rule / override refs), so an
 * edit to ONE task reuses every untouched series' occurrences instead of re-running
 * rrule for all of them — the bulk of the per-edit cost on large calendars.
 */
export function useExpandedWindow(
  visibleStart: Date,
  visibleCount: number,
  tasks: Task[],
  rules: Rules,
  overrides: Overrides,
): { days: DayCol[]; windowStart: Date } {
  const { windowStart, totalCount } = useDayWindow(visibleStart, visibleCount);
  const cache = useRef<Map<string, DayCol>>(new Map());
  const ruleCache = useRef<RuleExpansionCache>(getCalendarRuleExpansionCache());
  const days = useMemo(() => {
    const fresh = expandDayCols(tasks, rules, overrides, windowStart, totalCount, ruleCache.current);
    const next = new Map<string, DayCol>();
    const out = fresh.map((day) => {
      const iso = day.date.toISOString();
      const prev = cache.current.get(iso);
      const kept = prev && sameDay(prev, day) ? prev : day;
      next.set(iso, kept);
      return kept;
    });
    cache.current = next; // drop entries for days that scrolled out of the window
    return out;
  }, [tasks, rules, overrides, windowStart, totalCount]);
  return { days, windowStart };
}
